<!--
  Seletor de aulas para a elegibilidade de um plano.

  Não usa o componente Grid genérico: ele monta as colunas a partir do metadado
  do backend (que para aula só trazia `id`, e por isso o cabeçalho aparecia como
  "grid.id") e não expõe a seleção para slots customizados — sem isso não há como
  desabilitar a linha que não cumpre a cota nem explicar o porquê ao lado dela.

  A regra de quem pode ser escolhida é do servidor
  (`SubscriptionPlanService::lessonCandidates`), não daqui: ela cruza a grade
  semanal com a frequência da aula, e repetir essa conta no front seria uma
  segunda verdade fadada a divergir.
-->
<template>
  <vs-popup :title="$t('lesson.titles')" :active.sync="visible" id="popup-lesson" @close="closePopup" ref="vspopup-lesson">
    <div v-if="plan.classes_per_period" class="mb-2">
      <b-alert show variant="info" class="p-1 mb-0">
        <div class="flex items-start">
          <feather-icon icon="InfoIcon" class="mr-1 flex-shrink-0"/>
          <small>{{ $t('subscription_plan.candidates_help', {
            quota: plan.classes_per_period,
            period: (plan.quota_period_label || '').toLowerCase()
          }) }}</small>
        </div>
      </b-alert>
    </div>

    <b-input-group class="mb-2">
      <b-input-group-prepend is-text>
        <feather-icon icon="SearchIcon" class="h-4 w-4"/>
      </b-input-group-prepend>
      <b-form-input
        v-model="search"
        placeholder="Pesquisar"
        @keyup.enter="reload(true)"
        debounce="400"/>
    </b-input-group>

    <b-table
      :items="rows"
      :fields="fields"
      :busy="loading"
      small
      responsive
      striped
      show-empty
      :empty-text="$t('subscription_plan.no_lesson_candidates')">
      <template #cell(selected)="row">
        <b-form-checkbox
          :checked="isSelected(row.item.id)"
          @change="toggle(row.item)"/>
      </template>

      <template #cell(name)="row">
        <div class="font-medium">{{ row.item.name }}</div>
        <!-- Aviso, não bloqueio: a cota pode ser fechada por mais de uma aula —
             duas de 2x por semana atendem um plano de 3. Barrar cada uma pelo
             que ela sozinha não entrega impediria justamente a composição, que é
             o uso normal do escopo "somente as aulas escolhidas". -->
        <small v-if="!row.item.meets_plan" class="text-warning d-block">
          <feather-icon icon="AlertTriangleIcon" class="h-3 w-3"/>
          {{ row.item.reason }}
        </small>
      </template>

      <template #cell(schedules_count)="row">
        <div>{{ $t('subscription_plan.weekly_slots', { count: row.item.schedules_count }) }}</div>
        <small class="text-muted">
          {{ $t('subscription_plan.offers_in_period', { count: row.item.offered_in_period }) }}
        </small>
      </template>
    </b-table>

    <!-- Soma do que foi escolhido: sem isto o aviso por linha não tem desfecho —
         o clube vê "sozinha não fecha" e não sabe se a combinação fecha. A conta
         acompanha a seleção entre páginas. -->
    <div v-if="selected.length && required" class="border rounded p-1 mt-1 mb-1">
      <small :class="offeredTotal >= required ? 'text-success' : 'text-warning'">
        <feather-icon :icon="offeredTotal >= required ? 'CheckCircleIcon' : 'AlertTriangleIcon'" class="h-4 w-4"/>
        {{ $t('subscription_plan.selection_total', {
          offered: offeredTotal, required: required, period: (plan.quota_period_label || '').toLowerCase()
        }) }}
      </small>
    </div>

    <div class="flex flex-row justify-between items-center mt-2">
      <b-pagination
        v-if="lastPage > 1"
        v-model="page"
        :total-rows="total"
        :per-page="perPage"
        size="sm"
        class="mb-0"
        @input="reload(false)"/>
      <div v-else></div>

      <b-button @click="addLessons" variant="primary" :disabled="selected.length === 0">
        {{ $t('subscription_plan.add_selected_lessons', { count: selected.length }) }}
      </b-button>
    </div>
  </vs-popup>
</template>

<script>
import {
  BAlert, BButton, BFormCheckbox, BFormInput, BInputGroup, BInputGroupPrepend, BPagination, BTable,
} from 'bootstrap-vue'
import SubscriptionPlanService from '@/services/api/SubscriptionPlanService'

export default {
  components: {
    BAlert, BButton, BFormCheckbox, BFormInput, BInputGroup, BInputGroupPrepend, BPagination, BTable,
  },
  props: {
    visible: { type: Boolean, default: false },
    /** Plano dono da elegibilidade — é ele que define quais aulas servem. */
    planId: { type: [String, Number], required: true },
  },
  data: () => ({
    rows: [],
    plan: {},
    /** Itens inteiros, e não só ids: a soma precisa sobreviver à troca de página. */
    selected: [],
    search: '',
    page: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    loading: false,
  }),
  computed: {
    required() {
      return this.plan.classes_per_period || 0
    },
    offeredTotal() {
      return this.selected.reduce((sum, item) => sum + (item.offered_in_period || 0), 0)
    },
    fields() {
      return [
        { key: 'selected', label: '', thStyle: { width: '40px' } },
        { key: 'name', label: this.$t('lesson.name') },
        { key: 'modality_name', label: this.$t('lesson.modality') },
        { key: 'schedules_count', label: this.$t('subscription_plan.grid_column') },
      ]
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.selected = []
        this.reload(true)
      }
    },
    search() {
      this.reload(true)
    },
  },
  methods: {
    reload(resetPage) {
      if (!this.planId) return
      if (resetPage) this.page = 1

      this.loading = true
      SubscriptionPlanService.build(this.$vs)
        .getLessonCandidates(this.planId, { search: this.search, page: this.page, per_page: this.perPage })
        .then(data => {
          this.rows = data.data || []
          this.plan = data.plan || {}
          this.total = data.total || 0
          this.lastPage = data.last_page || 1
        })
        .catch(error => this.notifyErrors(this.$vs, error))
        .finally(() => { this.loading = false })
    },
    isSelected(id) {
      return this.selected.some(item => item.id === id)
    },
    toggle(item) {
      this.selected = this.isSelected(item.id)
        ? this.selected.filter(selected => selected.id !== item.id)
        : [...this.selected, item]
    },
    addLessons() {
      this.$emit('selected', this.selected.map(item => item.id))
      this.$emit('close', false)
    },
    closePopup(value) {
      this.$emit('close', value)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .vs-popup {
  width: 800px !important;
}
</style>
